import * as React from 'react';
import './relationshipField.scss';
import { FieldProps } from '../metadataFormField';
import { FieldGroup } from '../FieldGroup/fieldGroup';
import { useActions } from './relationshipModalActions';
import { TreeSelect } from '../../TreeSelect/treeSelect';
import { RelationshipModal } from './relationshipModal';
import { Icon } from 'rcd-cpp-ui/ui/icon/Icon';
import { Close } from 'rcd-cpp-ui/ui/icon/Remove';
import { Arrow } from 'rcd-cpp-ui/ui/icon/Arrow';
import { Metadata, Schema, SchemaField } from '../../../types';
import { useFullStory } from '../../App/fullStory';
import { TriggerLocation } from '../../../enums/TriggerLocation';
import { FSEvent } from '../../../enums/FSEvent';
import { ItemLink } from '../../ItemLink/itemLink';
import { RelationshipTypeId } from '../../../enums/RelationshipTypeId';
import {
  cn,
  getItemContentTypeId,
  getItemRelationshipFields,
  getItemStatusId,
  getReferenceValue,
  getRelationshipOf,
  getSchema,
  isRemovedOrInactive
} from '../../../utils';
import {
  isActiveDocument,
  isPublishedOrCompletedInCPP
} from '../../../utils/status';
import { useSelector } from '../../../utils/useSelector';
import { Spinner } from 'rcd-cpp-ui/ui/loader';
import { RelationshipFieldType } from '../../../enums/RelationshipFieldType';
import { NodeType } from '../../../enums/NodeType';
import { ItemType } from '../../../enums/ItemType';
import { ContentType } from '../../../enums/ContentType';
import { ItemStatus } from '../../../enums/ItemStatus';
import { useConfirmModal } from '../../ConfirmModal/confirmModal';

const RelationshipFSEvent = {
  [RelationshipFieldType.UNI]: FSEvent.UNI,
  [RelationshipFieldType.ProjectDerivatives]: FSEvent.ProjectDerivatives
};

const RelationshipPrefix = {
  [RelationshipTypeId.BranchContextualization]: 'Branch',
  [RelationshipTypeId.UpdateBranch]: 'Update Branch',
  [RelationshipTypeId.VariantContextualization]: 'Variant',
  [RelationshipTypeId.PerspectiveContextualization]: 'Perspective'
};

const UNI_REL_BREAK_EXCLUDED_CONTENT_TYPE: ContentType[] = [
  ContentType.HYPE_CYCLE,
  ContentType.MCP_Overview
];

const UNI_REL_BREAK_EXCLUDED_ITEM_STATUS: ItemStatus[] = [
  ItemStatus.Published,
  ItemStatus.Complete,
  ItemStatus.RetractedTemporary,
  ItemStatus.PublishedHistoricalNonCPP,
  ItemStatus.PendingRepublish
];

const ParentRelationshipType = {
  [RelationshipFieldType.Modification]: RelationshipFieldType.Association,
  [RelationshipFieldType.DPU]: RelationshipFieldType.Association
};
export const getIsChildSelect = (
  data: Metadata | Partial<Metadata>,
  type: RelationshipFieldType | string
) => {
  if (type == RelationshipFieldType.Series) {
    return true;
  }
  if (data?.props?.contentType?.length && type != RelationshipFieldType.UNI) {
    return data?.props?.contentType[0]?.props?.refId == 'dtyp:314';
  }
  return false;
};

export const getIsMultiSelect = (
  data: Metadata | Partial<Metadata>,
  type: RelationshipFieldType
) => {
  if (type == RelationshipFieldType.Series) {
    return true;
  }

  if (data?.props?.contentType?.length && type != RelationshipFieldType.UNI) {
    return data?.props?.contentType[0]?.props?.refId == 'dtyp:314';
  }
  return false;
};

export const RelationshipField = (props: FieldProps) => {
  const { data, updater, disabled } = props;
  const { updateField, update } = updater;
  const {
    createRelationshipInContent,
    createChildRelationshipInContent,
    removeProjectDerivativesInContent,
    createSeries
  } = useActions();
  const { confirm, ConfirmModal } = useConfirmModal();
  const { captureEvent } = useFullStory();
  const [expandedCard, setExpandedCard] = React.useState(null);
  const [showModal, setShowModal] = React.useState(false);
  const [selType, setSelType] = React.useState<RelationshipFieldType>(null);

  const itemSchema: Schema | null = useSelector(s =>
    getSchema(s.planning.selectedWorkspace?.schemas, NodeType.Item)
  );

  React.useEffect(() => {
    if (!!data.props.uniOf?.length || !!data.props.uniBy?.length) {
      setExpandedCard(RelationshipFieldType.UNI);
    } else if (data.props?.series?.length > 0) {
      setExpandedCard(RelationshipFieldType.Series);
    }
  }, []);

  const createChildRelationship = async (
    type: RelationshipFieldType,
    selectedChildren: Metadata[]
  ) => {
    if (type === RelationshipFieldType.Series) {
      if (data.props?.series?.length > 0) {
        for (const s of data.props.series) {
          await createSeries(data.props?.refId, s, selectedChildren);
        }
      } else {
        const series: Partial<Metadata> = {
          labels: [NodeType.Series],
          props: {
            name: ''
          }
        };
        await createSeries(data.props?.refId, series, selectedChildren);
      }
      setExpandedCard(RelationshipFieldType.Series);
      return;
    }

    if (!selectedChildren?.length) return;
    const { parentField, childField } = getItemRelationshipFields(
      itemSchema,
      type
    );

    if (!parentField) throw new Error('Missing parent field for type: ' + type);
    const planningChildren = selectedChildren?.filter(
      c => !c?.props?.itemId && !!c?.props?.refId
    );
    const contentChildren = selectedChildren?.filter(
      c => !!c?.props?.itemId && !!c?.props?.refId
    );
    if (data?.props?.itemId) {
      if (planningChildren?.length > 0) {
        updateField(childField, planningChildren);
        await update();
      }
      if (contentChildren?.length > 0) {
        await createChildRelationshipInContent(type, data, contentChildren);
      }
    } else {
      updateField(childField, selectedChildren);
      await update();
    }
    setExpandedCard(type);
    const fsEvent = RelationshipFSEvent[type];
    if (fsEvent) {
      const parentStatus = data?.props?.status[0];
      const parentStatusId = parentStatus?.props?.id;
      const parentStatusText = parentStatus?.props?.description;
      captureEvent(fsEvent, {
        triggerLocation: TriggerLocation.SETUP_RELATIONSHIP,
        parentStatusId,
        parentStatusText
      });
    }
  };

  const createParentRelationship = async (
    type: RelationshipFieldType,
    selectedParent: Metadata
  ) => {
    if (!selectedParent?.props?.refId) return;
    const { parentField } = getItemRelationshipFields(itemSchema, type);
    if (!parentField) throw new Error('Missing parent field for type: ' + type);
    if (data?.props?.itemId) {
      await createRelationshipInContent(type, data, selectedParent);
    } else {
      updateField(parentField, [selectedParent]);
      await update();
    }
    setExpandedCard(type);
    const fsEvent = RelationshipFSEvent[type];
    if (fsEvent) {
      const parentStatus = selectedParent?.props?.status[0];
      const parentStatusId = parentStatus?.props?.id;
      const parentStatusText = parentStatus?.props?.description;
      captureEvent(fsEvent, {
        triggerLocation: TriggerLocation.SETUP_RELATIONSHIP,
        parentStatusId,
        parentStatusText
      });
    }
  };

  const itemStatusId = getItemStatusId(data);
  const itemId = data?.props?.itemId;

  const handleRemoveParent = (
    type: RelationshipFieldType,
    parentField: SchemaField,
    parentType?: RelationshipFieldType
  ) => {
    if (type == RelationshipFieldType.Companion) return null;
    if (type == RelationshipFieldType.Contextualizations) return null;
    if (type == RelationshipFieldType.Translation) return null;
    if (parentType == RelationshipFieldType.Association) return null;
    if (type == RelationshipFieldType.UNI) {
      if (isRemovedOrInactive(itemStatusId)) return null;
      const contentTypeId = getItemContentTypeId(data);
      const isExcludedContentType =
        UNI_REL_BREAK_EXCLUDED_CONTENT_TYPE.includes(contentTypeId) ||
        getReferenceValue<boolean>(data, 'contentType', 'isBranded') ||
        getReferenceValue<boolean>(data, 'contentSubtype', 'isBranded');
      const isExcludedItemStatus =
        UNI_REL_BREAK_EXCLUDED_ITEM_STATUS.includes(itemStatusId) ||
        itemStatusId > ItemStatus.Published;
      const isExcluded = isExcludedContentType || isExcludedItemStatus;

      if (isExcluded && itemId != null) return null;
    }
    if (
      type == RelationshipFieldType.ProjectDerivatives &&
      !!itemId &&
      !isActiveDocument(itemStatusId)
    ) {
      return null;
    }
    return async (parent: Metadata) => {
      if (type == RelationshipFieldType.UNI) {
        if (!itemId || !parent?.props?.itemId) {
          updateField(parentField, []);
          await update();
        } else {
          confirm(
            <div>
              This item is currently in CPP Production. This change will be
              reflected within that system.
              <br />
              Are you sure you want to proceed?
            </div>,
            {
              header: 'Break UNI Relationship',
              onConfirm: async () => {
                await updateField(parentField, []);
                await update();
              }
            }
          );
        }
      } else if (type == RelationshipFieldType.ProjectDerivatives) {
        if (!itemId || !parent?.props?.itemId) {
          updateField(parentField, []);
          await update();
        } else if (isActiveDocument(itemStatusId)) {
          await removeProjectDerivativesInContent(data, parent, false);
        }
      }
    };
  };

  const handleRemoveChild = (
    type: RelationshipFieldType,
    childField: SchemaField,
    parentType?: RelationshipFieldType
  ) => {
    if (type == RelationshipFieldType.Companion) return null;
    if (type == RelationshipFieldType.Contextualizations) return null;
    if (type == RelationshipFieldType.Translation) return null;
    if (parentType == RelationshipFieldType.Association) return null;
    if (type == RelationshipFieldType.UNI && !!itemId) return null;
    if (
      type == RelationshipFieldType.ProjectDerivatives &&
      !!itemId &&
      !isActiveDocument(itemStatusId)
    ) {
      return null;
    }
    return async (child: Metadata, refId: string) => {
      let childItemId = child?.props?.itemId;
      if (
        [
          RelationshipFieldType.UNI,
          RelationshipFieldType.ProjectDerivatives
        ].includes(type) &&
        (!childItemId || !itemId)
      ) {
        updateField(
          childField,
          data?.props?.projectBy?.filter(c => c.props.refId != refId)
        );
        await update();
      } else if (
        type == RelationshipFieldType.ProjectDerivatives &&
        isActiveDocument(itemStatusId)
      ) {
        await removeProjectDerivativesInContent(child, data, true);
      }
    };
  };

  const relationshipTypes = React.useMemo(
    () =>
      [
        RelationshipFieldType.UNI,
        RelationshipFieldType.Contextualizations,
        RelationshipFieldType.ProjectDerivatives,
        RelationshipFieldType.Companion,
        RelationshipFieldType.DPU,
        RelationshipFieldType.Modification,
        RelationshipFieldType.Series,
        RelationshipFieldType.Translation
      ]
        .map(type => {
          if (!!data?.props && itemSchema) {
            const { childField, parentField, getParents, getChildren } =
              getItemRelationshipFields(itemSchema, type);
            const parentType: RelationshipFieldType =
              ParentRelationshipType[type];
            if (getParents(data)?.length > 0 || getChildren(data)?.length > 0) {
              return {
                parentType,
                type,
                parentField,
                childField,
                getParents,
                getChildren
              };
            }
          }
        })
        .filter(x => x),
    [itemSchema, data]
  );

  return (
    <FieldGroup
      {...props}
      value={null}
      disabled={false}
      componentRight={
        !props.disabled && showSetupRelationshipLink(data) ? (
          <div className="fgl-link" onClick={() => setShowModal(true)}>
            Set Up Relationship
          </div>
        ) : null
      }
    >
      <div className="relationship-container">
        {relationshipTypes.map(
          ({
            parentType,
            type,
            parentField,
            childField,
            getParents,
            getChildren
          }) => {
            return (
              <RelationshipFieldCard
                key={type}
                type={type}
                disabled={disabled}
                parentType={parentType}
                getParents={getParents}
                getChildren={getChildren}
                expandedCard={expandedCard}
                handleRemoveParent={handleRemoveParent(
                  type,
                  parentField,
                  parentType
                )}
                handleRemoveChild={handleRemoveChild(
                  type,
                  childField,
                  parentType
                )}
                data={data}
              />
            );
          }
        )}
        {relationshipTypes.length == 0 && <div className="dashes">--</div>}
      </div>
      {showModal && (
        <RelationshipModal
          data={data}
          onClose={() => setShowModal(false)}
          onSave={
            getIsChildSelect(data, selType)
              ? createChildRelationship
              : createParentRelationship
          }
          setSelType={setSelType}
          selType={selType}
        />
      )}
      <ConfirmModal />
    </FieldGroup>
  );
};

interface RelationshipFieldCardProps {
  type: RelationshipFieldType;
  parentType?: RelationshipFieldType;
  getParents: (data?: Partial<Metadata>) => Metadata[] | null;
  getChildren: (
    data?: Partial<Metadata>,
    parentRefId?: string
  ) => Metadata[] | null;
  expandedCard: RelationshipFieldType;
  handleRemoveParent: (parent: Metadata) => Promise<any>;
  handleRemoveChild: (child: Metadata, refId: string) => Promise<any>;
  disabled: boolean;
  data: Partial<Metadata>;
}
const RelationshipFieldCard = ({
  type,
  parentType,
  getParents,
  getChildren,
  expandedCard,
  handleRemoveParent,
  handleRemoveChild,
  disabled,
  data
}: RelationshipFieldCardProps) => {
  const [isExpanded, setIsExpanded] = React.useState(expandedCard == type);
  React.useEffect(() => {
    if (expandedCard) {
      setIsExpanded(expandedCard == type);
    }
  }, [expandedCard]);

  const parents = React.useMemo(() => {
    if (data?.props) {
      return getParents(data) ?? [];
    }
    return [];
  }, [data]);

  const children = React.useMemo(() => {
    if (data?.props) {
      const parents = getParents(data);
      if (parents?.length > 0) {
        return parents.flatMap(p => getChildren(data, p.props?.refId) ?? []);
      }
      return getChildren(data) ?? [];
    }
    return [];
  }, [data]);

  let label: string = type;
  if (type == RelationshipFieldType.Companion) {
    label = `${type} (${children.length + parents.length})`;
  }

  if (parentType == RelationshipFieldType.Association) {
    label = parentType;
  }

  if (!parents.length && !children.length) return null;

  const isRemoveDisabled = disabled || type === RelationshipFieldType.Series;
  return (
    <div>
      <div
        className={cn('relation-type-header', { collapse: !isExpanded })}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Arrow direction={isExpanded ? 'down' : 'right'} type="solid" />
        {label}
      </div>
      {isExpanded && (
        <RelationshipFieldValue
          data={data}
          children={children}
          parents={parents}
          relationshipType={type}
          parentType={parentType}
          onRemove={
            isRemoveDisabled
              ? null
              : getIsChildSelect(data, type)
                ? handleRemoveChild
                : handleRemoveParent
          }
        />
      )}
    </div>
  );
};

const itemComparator = (a: Metadata, b: Metadata) => {
  const ta: string = (a?.props?.title || '').trim();
  const tb: string = (b?.props?.title || '').trim();
  if (ta == tb) {
    const ia = '' + (a?.props?.itemId || '');
    const ib = '' + (b?.props?.itemId || '');
    return ia.localeCompare(ib);
  }
  return ta.localeCompare(tb);
};

type RFVProps = {
  data: Partial<Metadata>;
  children: Metadata[];
  parents: Metadata[];
  relationshipType: RelationshipFieldType;
  parentType?: RelationshipFieldType;
  onRemove: (parent: Metadata, refId?: string) => Promise<any>;
};

const RelationshipFieldValue = ({
  data,
  children,
  parents,
  relationshipType,
  parentType,
  onRemove
}: RFVProps) => {
  const [removingRefId, setRemovingRefId] = React.useState<string>(null);
  const treeData = React.useMemo(() => {
    const dataCopy = JSON.parse(JSON.stringify(data));
    let treeData = [dataCopy];
    if (relationshipType == RelationshipFieldType.Companion) {
      dataCopy.children = [...parents, ...children].sort(itemComparator);
    } else if (relationshipType === RelationshipFieldType.Series) {
      treeData = parents.map(p => {
        return {
          ...p,
          children: [...children].filter(c =>
            p?.props?.items?.some(
              (i: Metadata) => i.props.refId == c.props.refId
            )
          )
        };
      });
    } else {
      dataCopy.children = [...children].sort(itemComparator);
      if (parents?.length) {
        treeData = parents.map(p => {
          return { ...p, children: [dataCopy] };
        });
      }
    }
    return treeData;
  }, [data, parents, children, relationshipType]);

  return (
    <TreeSelect
      disabled={true}
      getData={treeData}
      getChildren={i => (!i.children ? [] : i.children)}
      selected={[data, ...parents, ...children]}
      renderData={i => {
        if (!i?.data) return null;
        const refId = i.data.props?.refId;

        const childIndex =
          children?.findIndex(c => c.props.refId == refId) ?? -1;
        const isChild = childIndex !== -1;
        const isParent = parents?.some(p => p.props.refId == refId);

        const titlePrefix =
          relationshipType === RelationshipFieldType.Series && isChild
            ? `${childIndex + 1} - `
            : '';
        const title = `${titlePrefix}${i.data.props?.title}`;

        let relationshipTypeText = '';
        const relationshipTypeId =
          i.data.relationship?.props?.relationshipTypeId ||
          (i.parents?.length == 1
            ? i.parents[0].relationship?.props?.relationshipTypeId
            : null);
        const prefix = RelationshipPrefix[relationshipTypeId];
        if (prefix && parentType != RelationshipFieldType.Association) {
          relationshipTypeText = `${prefix}: `;
        }

        if (
          relationshipType === RelationshipFieldType.Series &&
          i.parents?.length === 0
        ) {
          return <div>{children.length}-Part Series</div>;
        }

        if (isChild) {
          return (
            <div>
              <span className="relation-type-text">
                {relationshipType == RelationshipFieldType.UNI &&
                  relationshipType + 'ed By: '}
                {relationshipType == RelationshipFieldType.Translation &&
                  'Translation By: '}
                {parentType == RelationshipFieldType.Association &&
                  relationshipType + ` By: `}
                {relationshipType !== RelationshipFieldType.Series &&
                  relationshipType !== RelationshipFieldType.Translation &&
                  relationshipTypeText}
              </span>
              {refId === data.props?.refId &&
              relationshipType === RelationshipFieldType.Series ? (
                <span>{title}</span>
              ) : (
                <ItemLink refId={refId} title={title} />
              )}
              {removingRefId == refId && (
                <Icon>
                  <Spinner spinnerSize="icon" />
                </Icon>
              )}
              {!!onRemove &&
                removingRefId != refId &&
                relationshipType != RelationshipFieldType.UNI && (
                  <Icon
                    onClick={async () => {
                      setRemovingRefId(refId);
                      await onRemove(i.data, refId);
                      setRemovingRefId(null);
                    }}
                  >
                    <Close />
                  </Icon>
                )}
            </div>
          );
        } else if (isParent) {
          return (
            <div>
              <span className="relation-type-text">
                {[
                  RelationshipFieldType.DPU,
                  RelationshipFieldType.Modification,
                  RelationshipFieldType.UNI
                ].includes(relationshipType) && relationshipType + ' Of: '}
                {relationshipType == RelationshipFieldType.Translation &&
                  'Translation Of: '}
                {relationshipType == RelationshipFieldType.ProjectDerivatives &&
                  'Project: '}
              </span>

              <ItemLink refId={refId} title={title} />
              {removingRefId == refId && (
                <Icon>
                  <Spinner spinnerSize="icon" />
                </Icon>
              )}
              {!!onRemove && removingRefId != refId && (
                <Icon
                  onClick={async () => {
                    setRemovingRefId(refId);
                    await onRemove(i.data);
                    setRemovingRefId(null);
                  }}
                >
                  <Close />
                </Icon>
              )}
            </div>
          );
        } else {
          return (
            <div>
              <span className="relation-type-text">
                {relationshipType == RelationshipFieldType.ProjectDerivatives &&
                  !parents?.length &&
                  'Project: '}
                {relationshipTypeText}
              </span>
              {title || '<Title Not Defined>'}
            </div>
          );
        }
      }}
      getUniqueKey={i => i?.props?.refId}
      onChange={() => {}}
      isHidden={() => false}
      settings={[{}, {}, {}]}
    />
  );
};

const showSetupRelationshipLink = (item: Partial<Metadata>) => {
  const statusId = getItemStatusId(item);
  if (
    (isActiveDocument(statusId) || isPublishedOrCompletedInCPP(statusId)) &&
    (!item?.props?.uniOf?.length || !item?.props?.projectOf?.length) &&
    !getRelationshipOf(item, [
      RelationshipTypeId.PerspectiveContextualization,
      RelationshipTypeId.VariantContextualization
    ]).length &&
    item?.props?.itemType == ItemType.content
  ) {
    return true;
  }
  return false;
};
